/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HTMLAttributes } from 'react';
import React, { useMemo } from 'react';
import type { Cell, Column, HeaderGroup, Row, SortingRule } from 'react-table';
import { useExpanded, usePagination, useSortBy, useTable } from 'react-table';
import { useSticky } from 'react-table-sticky';
import classnames from 'classnames';
import type { CustomColumnProps, HeaderCellGroup } from './types';
import type { AllColorType } from '../../util/types';
import { TableStyled } from './Table.styled';
import { Footer, HeaderCell, Pagination } from './components';
import { TABLE_WRAPPER } from '../../util/data-id';
import RowMemoized from './components/RowMemoized';
import Loader from '../loader/Loader';
import NoData from '../../global/sections/NoData';

type Ref = HTMLTableElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  /**
   * Table identification.
   */
  id: string;
  /**
   * Table columns.
   * Overrides react-table Column type in order to allow adding your own accessor types.
   * https://react-table.tanstack.com/docs/api/useTable#column-options
   */
  cols: CustomColumnProps[] | Column<any | Record<string, any>>[];
  /**
   * Table data.
   */
  content: Record<any, any>[];
  /**
   * Sets initial sort by.
   */
  initialState: {
    sortBy: SortingRule<unknown>[];
    pageSize?: number;
    hiddenColumns?: string[];
  };
  /**
   * Adds a custom message when the table is empty.
   */
  noDataMessage?: string | React.ReactNode;
  /**
   * Sets table color.
   */
  color?: AllColorType;
  /**
   * Adds border to table's tbody tag.
   */
  showTbodyBorder?: boolean;
  /**
   * Footer position
   */
  footerPosition?: 'top' | 'bottom' | null;
  /**
   * Style row
   */
  getRowProps?: (row: Row) => Record<string, unknown>;
  /**
   * Style cell
   */
  getCellProps?: (cell: Cell, row: Row) => Record<string, unknown>;
  /**
   * Removes unsorted state when a column is sorted
   */
  disableSortRemove?: boolean,
  /**
   * A custom component to insert as a header row
   */
  customHeaderComponent?: JSX.Element | null;
  /**
   * When false, prevents sortBy state to reset when data is changed
   */
  autoResetSortBy?: boolean,
  /**
   * Enable sticky colums and headers
   */
  sticky?: boolean;
  stickyTop?: number;
  truncate?: boolean;
  scrollContainer?: Window | Element | null;
  newId?: number;
}

const Table = React.forwardRef<Ref, Props>((
  {
    id,
    cols,
    content,
    initialState,
    noDataMessage,
    color = 'primary',
    showTbodyBorder = false,
    footerPosition = 'bottom',
    getRowProps = () => ({}),
    getCellProps = () => ({}),
    disableSortRemove = false,
    customHeaderComponent,
    autoResetSortBy = true,
    sticky = false,
    stickyTop = 0,
    truncate = false,
    scrollContainer,
    newId,
  }: Props, ref,
): JSX.Element => {

  const columns = useMemo(() => cols, [ cols ]);
  const data = useMemo(() => content, [ content ]);

  const {
    // Base
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    page,
    nextPage,
    previousPage,
    gotoPage,
    canNextPage,
    canPreviousPage,
    pageCount,
    rows,
    prepareRow,
    state: {
      expanded,
      pageIndex,
      sortBy,
    },
  } = useTable(
    {
      columns,
      data,
      // TODO Review this
      initialState: {
        pageSize: -1,
        ...initialState,
      },
      disableSortRemove: disableSortRemove,
      autoResetSortBy: autoResetSortBy,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useSticky,
  );

  const pagination = pageCount > 1;
  const showFastJumpButton = pageCount > 2;

  const getBodyRows = () => {

    let count = 0;
    const data_source = pagination ? page : rows;
    const sortedBy = sortBy.map((sort) => sort.id);

    return data_source.map((row) => {
      prepareRow(row);

      if (!row.depth) {
        count++;
      }

      return (
        <RowMemoized
          key={`${id}--${row.getRowProps().key}`}
          row={row}
          count={count}
          getRowProps={getRowProps}
          getCellProps={getCellProps}
          tableId={id}
          sortedBy={sortedBy}
          newId={newId}
        />
      );
    });
  };

  const noData = data.length === 0;
  const isLoading = !noData && rows?.length === 0;

  return (
    <>
      { isLoading ? <Loader /> : null }
      <TableStyled data-id={TABLE_WRAPPER} $color={color} $showTbodyBorder={showTbodyBorder} $stickyTop={stickyTop}>
        <table ref={ref} id={id} data-expanded={Object.keys(expanded).length !== 0} className={classnames({ 'sticky-table': sticky })} {...getTableProps()} >
          <thead>
            {customHeaderComponent}
          </thead>
          <thead className={classnames({ 'hidden': isLoading || noData })}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={`${id}--${headerGroup.getHeaderGroupProps().key}`}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    key={`${id}--${column.getHeaderProps().key}`}
                    data-id={`col_${column.id}`}
                    title={''}
                  >
                    <HeaderCell
                      column={column as HeaderCellGroup}
                      truncate={truncate}
                    >
                      {column.render('Header')}
                    </HeaderCell>
                  </th>
                ))}
              </tr>
            ))}
            {'top' === footerPosition
              ? (<FooterAsHeader footerGroups={footerGroups as HeaderGroup<Record<string, unknown>>[]}/>)
              : null
            }
          </thead>
          <tbody {...getTableBodyProps()} data-cy="table">
            {0 === rows?.length ? (
              <tr>
                <td colSpan={columns.length} className={'italic'}>
                  {noDataMessage ?? <NoData />}
                </td>
              </tr>
            ) : getBodyRows()}
          </tbody>
          {'bottom' === footerPosition
            ? (
              <tfoot>
                <Footer footerGroups={footerGroups as HeaderGroup<Record<string, unknown>>[]} />
              </tfoot>
            )
            : null
          }
        </table>
      </TableStyled>
      {
        pagination && (
          <div className={'mt-lg mx-auto'}>
            <Pagination
              pageIndex={pageIndex}
              pageCount={pageCount}
              showFastJumpButton={showFastJumpButton}
              canPreviousPage={canPreviousPage}
              previousPage={previousPage}
              canNextPage={canNextPage}
              nextPage={nextPage}
              gotoPage={gotoPage}
              scrollContainer={scrollContainer}
            />
          </div>
        )
      }
    </>
  );
});

const FooterAsHeader = ({ footerGroups }: { footerGroups: HeaderGroup<Record<string, unknown>>[] }): JSX.Element => (
  <React.Fragment>
    {footerGroups.map((group) => (
      <tr {...group.getHeaderGroupProps()}>
        {group.headers.map((column) => (
          <td
            {...column.getHeaderProps()}
            data-id={`footer_${column.id}`}
          >
            {column.render('Footer')}
          </td>
        ))}
      </tr>
    ))}
  </React.Fragment>);

export default Table;
